import React from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import { uid } from 'uid';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PropsFromRedux } from '.';
import { getRequiredErrorMessage } from '../../utils/common.utils';
import { HAULAGE_TYPES } from '../../interfaces/shipping.interface';
import { useShippingFormContext } from '../../context/ShippingFormContext/context';
import { SHIPPING_FORM_TYPES } from '../../context/ShippingFormContext/reducer';
import { Spinner } from '../Spinners/Spinner';

interface Props extends PropsFromRedux {}

const schema = yup.object().shape({
  tonnage: yup
    .number()
    .integer()
    .min(1, 'Value must be a number greater than 1')
    .required(getRequiredErrorMessage('Tonnage'))
    .typeError('Tonnage must be a selected number'),
});

interface Inputs {
  tonnage: number;
}

const Tonnage: React.FC<Props> = ({ hideModal, haulage, getHaulage }) => {
  const history = useHistory();

  const { dispatch } = useShippingFormContext();

  const { handleSubmit, register, watch, errors } = useForm<Inputs>({
    resolver: yupResolver(schema),
  });

  React.useEffect(() => {
    getHaulage();
  }, [getHaulage]);

  const tonValue = watch('tonnage');

  if (haulage?.loading) {
    return (
      <div className="flex bg-white p-4 items-center justify-center">
        <Spinner color="#CCC" />
      </div>
    );
  }

  if (haulage.error && !haulage.loading) {
    return (
      <div className="flex bg-white p-4 flex-col items-center flex-wrap justify-center">
        <p>Could not fetch truck types types at the moment.</p>
        <div className="mt-4">
          <button
            className="bg-secondary py-2 px-6 text-white rounded"
            type="button"
            aria-label="Retry"
            onClick={() => getHaulage()}
          >
            Try Again
          </button>
          <button
            className="border-secondary border py-2 px-6 text-secondary rounded"
            type="button"
            aria-label="Retry"
            onClick={() => hideModal()}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const haulageTypes: HAULAGE_TYPES[] = haulage?.response?.Object;

  const onSubmit = ({ tonnage }: Inputs) => {
    const selectedTonnage = haulageTypes?.find(
      (haul) => haul.HaulageId === tonnage
    );

    if (selectedTonnage) {
      dispatch({
        type: SHIPPING_FORM_TYPES.UPDATE_VALUE,
        values: {
          HaulageId: selectedTonnage?.HaulageId,
          PreShipmentItems: [
            {
              ItemName: `${selectedTonnage.Tonne} Tonnes`,
              Description: selectedTonnage.Description,
              ItemType: 'Haulage',
              Quantity: 1,
              ShipmentType: 'Regular',
              SpecialPackageId: 0,
              WeightRange: '',
              Value: selectedTonnage.FixedRate,
              ImageUrl: '',
              Weight: selectedTonnage.Tonne * 1000,
              ShipmentItemId: uid(8),
            },
          ],
        },
      });
      history.push('/giggo-delivery-app/shipping-information');
      hideModal();
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full px-4 bg-white rounded-xl lg:px-14 py-10 relative"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => {
          hideModal();
        }}
      >
        <IoCloseSharp className="sticky sm:absolute cursor-pointer text-2xl top-0 left-full sm:left-auto sm:right-5 sm:top-5" />
      </button>

      <div className="w-full text-center mb-10">
        <h4 className="text-3xl mb-5 font-bold">Select truck size</h4>
        <p className="text-xl font-medium">
          Kindly select tonne size of a truck
        </p>
      </div>
      <div className="w-full max-w-xs mx-auto">
        {haulageTypes?.map((tonnage: HAULAGE_TYPES) => (
          <label htmlFor={`${tonnage.HaulageId}`} key={tonnage.HaulageId}>
            <div
              className={`bg-gray-200 cursor-pointer p-4 my-5 rounded-lg flex items-center justify-center font-medium ${
                tonValue?.toString() === tonnage.HaulageId.toString()
                  ? 'font-bold bg-gray-300'
                  : ''
              }`}
            >
              <p className="text-sm">{tonnage.Tonne}</p>
            </div>
            <input
              id={`${tonnage.HaulageId}`}
              name="tonnage"
              value={tonnage.HaulageId}
              className="hidden"
              ref={register}
              type="radio"
            />
          </label>
        ))}
        {errors['tonnage'] ? (
          <span className="text-xs text-red-500">
            {errors['tonnage'].message}
          </span>
        ) : (
          ''
        )}
      </div>
      <div className="w-full my-10 flex items-center justify-center">
        <button
          className="h-12 flex justify-center bg-secondary rounded px-12 text-white items-center font-bold text-lg"
          type="submit"
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default Tonnage;
